import { Chip, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import type { TFunction } from "i18next";
import type { HeatQualityFlagDetail } from "src/store/api/generatedApi";
import { FLAG_TRANSLATION_KEY } from "components/heatQualityIssues/issueLabels";

dayjs.extend(utc);

interface OutOfRangeBucket {
  bucket_id: string;
  bucket_mass: number;
}

interface FlagDetails {
  reported_charged_mass?: number;
  scrapeye_charged_mass?: number;
  out_of_range_buckets?: OutOfRangeBucket[];
  count?: number;
  min?: number;
  max?: number;
  basket_numbers?: number[];
  buckets?: {
    bucket_id: string;
    basket_numbers?: number[];
    load_ids?: number[];
  }[];
  conflicts?: { basket_number: number; bucket_ids: string[] }[];
  windows?: { bucket_id: string; minutes: number }[];
  sessions?: { session_start_timestamp?: string; num_missed_drops?: number }[];
}

const flagDetailLine = (
  flag: HeatQualityFlagDetail,
  t: TFunction
): string | null => {
  const details = flag.details as FlagDetails | null;
  if (details == null) {
    return null;
  }
  switch (flag.code) {
    case "basket_count_out_of_range":
      if (details.count == null) {
        return null;
      }
      return t("basketCountDetail", {
        count: details.count,
        min: details.min,
        max: details.max,
      });
    case "basket_numbers_not_contiguous": {
      const present = details.basket_numbers ?? [];
      if (present.length === 0) {
        return null;
      }
      return t("ordinalChargesDetail", { baskets: present.join(", ") });
    }
    case "charged_mass_deviation":
      if (
        details.reported_charged_mass == null ||
        details.scrapeye_charged_mass == null
      ) {
        return null;
      }
      return t("reportedVsScrapeyeMass", {
        reported: Math.round(details.reported_charged_mass),
        scrapeye: Math.round(details.scrapeye_charged_mass),
      });
    case "bucket_mass_out_of_range": {
      const buckets = details.out_of_range_buckets ?? [];
      if (buckets.length === 0) {
        return null;
      }
      const rendered = buckets
        .map(
          (bucket) =>
            `${bucket.bucket_id} (${Math.round(bucket.bucket_mass)} t)`
        )
        .join(", ");
      return `${t("outOfRangeBaskets")}: ${rendered}`;
    }
    case "multiple_basket_numbers_per_bucket": {
      const buckets = details.buckets ?? [];
      if (buckets.length === 0) {
        return null;
      }
      return buckets
        .map(
          (bucket) =>
            `${bucket.bucket_id} → ${(bucket.basket_numbers ?? []).join(", ")}`
        )
        .join("; ");
    }
    case "multiple_loads_per_heat_bucket": {
      const buckets = details.buckets ?? [];
      if (buckets.length === 0) {
        return null;
      }
      return buckets
        .map(
          (bucket) =>
            `${bucket.bucket_id} → ${(bucket.load_ids ?? []).join(", ")}`
        )
        .join("; ");
    }
    case "bucket_loading_window_too_long": {
      const windows = details.windows ?? [];
      if (windows.length === 0) {
        return null;
      }
      return windows
        .map((window) => `${window.bucket_id} (${window.minutes} min)`)
        .join("; ");
    }
    case "missed_consumption_drops": {
      const rendered = (details.sessions ?? [])
        .filter(
          (session) =>
            session.num_missed_drops != null &&
            session.session_start_timestamp != null
        )
        .map((session) =>
          t("missedConsumptionDropsDetail", {
            count: session.num_missed_drops,
            date: dayjs
              .utc(session.session_start_timestamp)
              .format("YYYY-MM-DD HH:mm"),
          })
        );
      return rendered.length === 0 ? null : rendered.join("; ");
    }
    case "duplicate_basket_number_across_buckets": {
      const conflicts = details.conflicts ?? [];
      if (conflicts.length === 0) {
        return null;
      }
      return conflicts
        .map(
          (conflict) =>
            `${conflict.basket_number}: ${conflict.bucket_ids.join(", ")}`
        )
        .join("; ");
    }
    default:
      return null;
  }
};

// One row per flag: the warning pill next to that flag's detail line, stacked.
// With showDetails=false, only the pills are rendered (wrapped in a row).
export const HeatQualityFlagRows = ({
  flags,
  showDetails = true,
}: {
  flags: HeatQualityFlagDetail[];
  showDetails?: boolean;
}) => {
  const { t } = useTenantTranslation();
  if (!showDetails) {
    return (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
        {flags.map((flag) => (
          <Chip
            key={flag.code}
            label={t(FLAG_TRANSLATION_KEY[flag.code])}
            size="small"
            color="warning"
            sx={{ fontSize: 9, height: 18 }}
          />
        ))}
      </Stack>
    );
  }
  return (
    <Stack spacing={0.5}>
      {flags.map((flag) => {
        const detail = flagDetailLine(flag, t);
        return (
          <Stack
            key={flag.code}
            direction="row"
            spacing={0.5}
            sx={{ alignItems: "flex-start", flexWrap: "wrap" }}
          >
            <Chip
              label={t(FLAG_TRANSLATION_KEY[flag.code])}
              size="small"
              color="warning"
              sx={{ fontSize: 9, height: 18, flexShrink: 0 }}
            />
            {detail != null ? (
              <Typography variant="caption" color="text.secondary">
                {detail}
              </Typography>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
};
